import { useEffect, useState } from 'react'
import { isPreviewServer, previewEntry } from './lib/previewEnter'
import { useServer } from '@/state/server'
import { pushTarget } from '@/lib/pushNav'
import { ensureRegistered } from '@/push'
import { useStream } from '@/state/stream'
import { RouterProvider } from '@tanstack/react-router'
import { api, db, engine } from './runtime'
import { META } from './db/schema'
import { router } from './router'
import { TokenScreen } from './components/TokenScreen'
import { BootstrapScreen } from './components/BootstrapScreen'
import { hydrateUi } from './state/ui'
import { hydrateScope } from './state/scope'
import { compareServerVersion, registerPeriodicSync } from './sw-register'
import { ensureSettings } from './lib/settingsWrite'
import { SharedView } from './views/Shared'
import { accessOf, recalledAccess, rememberAccess, useAccess } from './state/access'

type Phase = 'loading' | 'token' | 'bootstrap' | 'ready'

/** Gate: token → first bootstrap → the app (which renders only from Dexie). */
export const App = () => {
  const [phase, setPhase] = useState<Phase>('loading')
  useEffect(() => {
    void (async () => {
      // an agent's preview signs in from its link, and only on a preview server
      const entry = previewEntry(window.location.pathname, window.location.search)
      if (entry) {
        if (await isPreviewServer(entry.token)) {
          await db.setMeta(META.token, entry.token)
          await db.setMeta(META.deviceName, 'Preview')
        }
        window.history.replaceState(null, '', entry.next)
      }
      const [token, booted, access] = await Promise.all([
        db.getMeta<string>(META.token),
        db.getMeta<string>(META.bootstrappedAt),
        recalledAccess(db),
      ])
      useAccess.setState(access)
      if (!token) setPhase('token')
      else if (!booted) setPhase('bootstrap')
      else setPhase('ready')
    })()
  }, [])
  useEffect(() => {
    if (phase !== 'ready') return
    void hydrateUi()
    void db.resources
      .where('kind')
      .equals('Workspace')
      .toArray()
      .then((ws) =>
        hydrateScope(
          ws
            .filter((w) => !w.deleted_at && !(w.status as { archived_at?: string }).archived_at)
            .map((w) => w.spec as { slug: string; order?: number }),
        ),
      )
    // the agent's reply as it is written; the finished turn still arrives through sync
    engine.onAgentNote = (n) => useStream.getState().note(n)
    engine.start()
    // a device row can be forgotten or lost; say we are here again, with the
    // push subscription this browser still holds
    void ensureRegistered()
    void engine.sync()
    const onMessage = (ev: MessageEvent) => {
      // only paths inside the app: a push payload comes from our server, but the
      // worker message channel is not a place to trust a URL from
      const url = ev.data?.type === 'navigate' ? pushTarget(ev.data?.url) : null
      // href, not to: a path may carry a query (/agents/graph?mode=infra, /leave?to=…)
      if (url) void router.navigate({ href: url })
    }
    navigator.serviceWorker?.addEventListener('message', onMessage)
    void registerPeriodicSync()
    // a deploy shows up as a version the server reports that this bundle doesn't match
    const checkVersion = () =>
      void api
        .config()
        .then((c) => {
          compareServerVersion(c.version)
          useServer.getState().set({ version: c.version, note: c.note })
          // a member signed in before the server named her handle, or the owner made her an admin
          // (or took it back), learns it here; a changed reach is a fresh bootstrap, since the
          // changelog since her last pull holds none of what she newly sees
          if (c.member) {
            const was = useAccess.getState()
            const now = accessOf(c)
            const same =
              was.from === now.from &&
              was.name === now.name &&
              was.admin === now.admin &&
              was.settingsId === now.settingsId
            if (!same) {
              void rememberAccess(db, now).then(() => {
                if (was.admin !== now.admin) void engine.bootstrap().catch(() => {})
              })
            }
          }
        })
        .catch(() => {})
    checkVersion()
    const onVisible = () => {
      if (document.visibilityState === 'visible') checkVersion()
    }
    document.addEventListener('visibilitychange', onVisible)
    // make sure the Settings resource exists so preferences sync everywhere
    // (a member's Settings stay on their device: the server keeps the owner's)
    void ensureSettings()
    return () => {
      engine.stop()
      navigator.serviceWorker?.removeEventListener('message', onMessage)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [phase])
  // /s/<secret>: the share page needs no device or token of its own
  const shared = /^\/s\/([A-Za-z0-9_-]{20,})$/.exec(window.location.pathname)
  if (shared) return <SharedView secret={shared[1]!} />
  if (phase === 'loading') return null
  if (phase === 'token') return <TokenScreen onSaved={() => setPhase('bootstrap')} />
  if (phase === 'bootstrap')
    return <BootstrapScreen onDone={() => setPhase('ready')} onBadToken={() => setPhase('token')} />
  return <RouterProvider router={router} />
}
